import * as React from "react"
import { ChevronDown } from "lucide-react"

import { cn } from "@/lib/utils"

/**
 * Custom, dependency-free Accordion.
 *
 * SEO-critical difference from Radix: the content of every item is ALWAYS
 * rendered into the DOM (even when the item is closed). Closed content is only
 * VISUALLY collapsed via CSS (grid-template-rows 0fr -> 1fr), never removed and
 * never injected on click. This guarantees FAQ answers ship in the prerendered
 * HTML for crawlers and AI bots.
 *
 * Public API is compatible with the previous Radix-based component:
 *   <Accordion type="single" collapsible [value] [defaultValue] [onValueChange]>
 *     <AccordionItem value="...">
 *       <AccordionTrigger>...</AccordionTrigger>
 *       <AccordionContent>...</AccordionContent>
 *     </AccordionItem>
 *   </Accordion>
 */

const AccordionContext = React.createContext(null)
const AccordionItemContext = React.createContext(null)

const Accordion = React.forwardRef(
  ({ type = "single", collapsible = false, value, defaultValue, onValueChange, className, children, ...props }, ref) => {
    const isControlled = value !== undefined
    const [internal, setInternal] = React.useState(
      defaultValue !== undefined ? defaultValue : type === "multiple" ? [] : ""
    )
    const current = isControlled ? value : internal

    const commit = React.useCallback(
      (next) => {
        if (!isControlled) setInternal(next)
        if (onValueChange) onValueChange(next)
      },
      [isControlled, onValueChange]
    )

    const toggle = React.useCallback(
      (itemValue) => {
        if (type === "multiple") {
          const arr = Array.isArray(current) ? current : []
          commit(arr.includes(itemValue) ? arr.filter((v) => v !== itemValue) : [...arr, itemValue])
        } else if (current === itemValue) {
          commit(collapsible ? "" : itemValue)
        } else {
          commit(itemValue)
        }
      },
      [current, type, collapsible, commit]
    )

    const isOpen = React.useCallback(
      (itemValue) =>
        type === "multiple"
          ? Array.isArray(current) && current.includes(itemValue)
          : current === itemValue,
      [current, type]
    )

    const ctx = React.useMemo(() => ({ toggle, isOpen }), [toggle, isOpen])

    return (
      <div ref={ref} className={className} data-orientation="vertical" {...props}>
        <AccordionContext.Provider value={ctx}>{children}</AccordionContext.Provider>
      </div>
    )
  }
)
Accordion.displayName = "Accordion"

const AccordionItem = React.forwardRef(({ value, className, children, ...props }, ref) => {
  const reactId = React.useId()
  const itemCtx = React.useMemo(
    () => ({ value, triggerId: `acc-trigger-${reactId}`, contentId: `acc-content-${reactId}` }),
    [value, reactId]
  )
  return (
    <AccordionItemContext.Provider value={itemCtx}>
      <div ref={ref} className={cn("border-b", className)} {...props}>
        {children}
      </div>
    </AccordionItemContext.Provider>
  )
})
AccordionItem.displayName = "AccordionItem"

const AccordionTrigger = React.forwardRef(({ className, children, onClick, ...props }, ref) => {
  const acc = React.useContext(AccordionContext)
  const item = React.useContext(AccordionItemContext)
  const open = acc && item ? acc.isOpen(item.value) : false
  return (
    <h3 className="flex">
      <button
        type="button"
        ref={ref}
        id={item?.triggerId}
        aria-expanded={open}
        aria-controls={item?.contentId}
        data-state={open ? "open" : "closed"}
        onClick={(e) => {
          if (onClick) onClick(e)
          if (acc && item) acc.toggle(item.value)
        }}
        className={cn(
          "flex flex-1 items-center justify-between py-4 text-sm font-medium transition-all hover:underline text-left [&[data-state=open]>svg]:rotate-180",
          className
        )}
        {...props}
      >
        {children}
        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200" />
      </button>
    </h3>
  )
})
AccordionTrigger.displayName = "AccordionTrigger"

const AccordionContent = React.forwardRef(({ className, children, style, ...props }, ref) => {
  const acc = React.useContext(AccordionContext)
  const item = React.useContext(AccordionItemContext)
  const open = acc && item ? acc.isOpen(item.value) : false
  return (
    <div
      ref={ref}
      id={item?.contentId}
      role="region"
      aria-labelledby={item?.triggerId}
      data-state={open ? "open" : "closed"}
      className="grid overflow-hidden text-sm transition-[grid-template-rows] duration-200 ease-out"
      style={{ gridTemplateRows: open ? "1fr" : "0fr", ...style }}
      {...props}
    >
      <div className="min-h-0 overflow-hidden">
        <div className={cn("pb-4 pt-0", className)}>{children}</div>
      </div>
    </div>
  )
})
AccordionContent.displayName = "AccordionContent"

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent }
